import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { pushToastAtom } from '@/store';

/** Toasts (spec §9.4): mutations report failures here rather than inventing their own banner. */
export function useToast(): {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
} {
  const push = useSetAtom(pushToastAtom);
  return {
    success: useCallback((message: string) => push({ message, severity: 'success' }), [push]),
    error: useCallback((message: string) => push({ message, severity: 'error' }), [push]),
    info: useCallback((message: string) => push({ message, severity: 'info' }), [push]),
    warning: useCallback((message: string) => push({ message, severity: 'warning' }), [push]),
  };
}

/** Debounced mirror of a value — search boxes type faster than the API should be asked. */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export interface Hotkey {
  /** Lower-case `event.key`, e.g. `s`, `enter`, `arrowdown`. */
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  handler: (event: KeyboardEvent) => void;
}

/**
 * Window-level shortcuts (spec §9.3: Ctrl+S save all, Ctrl+Enter save + reviewed, Alt+↓ next).
 *
 * Bound on the window rather than on the grid because the shortcut has to work while the focus is
 * inside a cell input; the handler decides whether that matters, not the binding.
 */
export function useHotkeys(hotkeys: Hotkey[], enabled = true): void {
  const latest = useRef(hotkeys);

  // Written in an effect, not during render: a ref is not allowed to change while rendering.
  useEffect(() => {
    latest.current = hotkeys;
  });

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const pressed = event.key.toLowerCase();
      for (const hotkey of latest.current) {
        if (hotkey.key !== pressed) continue;
        if (Boolean(hotkey.ctrl) !== (event.ctrlKey || event.metaKey)) continue;
        if (Boolean(hotkey.alt) !== event.altKey) continue;
        if (Boolean(hotkey.shift) !== event.shiftKey) continue;
        event.preventDefault();
        hotkey.handler(event);
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

export {
  isAttentionUnread,
  attentionId,
  pruneAttentionReadMarks,
  useAttention,
  type Attention,
  type AttentionItem,
  type AttentionReadMarks,
} from './useAttention';
